import classNames from 'classnames';
import styles from './FJVideoPreviewItem.module.less';
import { useState, forwardRef, useImperativeHandle, useRef, useEffect, useCallback } from 'react';
import FJConstant from '../../../constants/FJConstant';
import FJUtil from '../../../util/FJUtil';
import PropTypes from 'prop-types';
import FJCanvasUtil from '../../../util/FJCanvasUtil';

const FJVideoPreviewItem = forwardRef((props, ref) => {
    const {
        videoUrl,
        thumbnailUrl,

        isHover = false,
        isLoading = false,
        isTransparentBG = false,

        sourceLoadStatus,
        setSourceLoadStatus,
    } = props;

    const [stateThumbnailUrl, setStateThumbnailUrl] = useState(thumbnailUrl);
    const [stateVideoUrl, setStateVideoUrl] = useState(videoUrl);
    const [stateVideoStatus, setStateVideoStatus] = useState(FJConstant.LOADING);

    const transparentBgCanvasRef = useRef(null);
    const videoRef = useRef(null);
    const loading = isLoading || sourceLoadStatus === FJConstant.LOADING;

    useEffect(() => {
        setStateVideoUrl(videoUrl);
    }, [videoUrl]);

    useEffect(() => {
        if (!videoRef.current) return;
        if (isHover) {
            videoRef.current.play();
        } else {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    }, [isHover]);

    useEffect(() => {
        if (isTransparentBG && transparentBgCanvasRef.current) {
            const canvas = transparentBgCanvasRef.current;
            const rect = canvas.getBoundingClientRect();
            const ctx = canvas.getContext('2d');
            canvas.width = rect.width;
            canvas.height = rect.height;

            FJCanvasUtil.drawTransparentBackground(ctx, rect.width, rect.height, 10);
        }
    }, [isTransparentBG]);

    const _videoReload = useCallback(() => {
        setStateVideoStatus(FJConstant.LOADING);
        setStateVideoUrl(FJUtil.getReloadNewUrl(videoUrl));
    }, [videoUrl]);

    const _sourceReload = () => {
        setSourceLoadStatus(FJConstant.LOADING);
        setStateThumbnailUrl(FJUtil.getReloadNewUrl(stateThumbnailUrl));
        if (stateVideoStatus === FJConstant.ERROR) {
            _videoReload();
        }
    };

    useImperativeHandle(ref, () => ({
        sourceReload: _sourceReload,
    }));

    useEffect(() => {
        if (isHover && stateVideoStatus === FJConstant.ERROR) {
            _videoReload();
        }
    }, [stateVideoStatus, isHover, _videoReload]);

    const onCanPlay = () => {
        setStateVideoStatus(FJConstant.SUCCESS);
        if (isHover && videoRef.current) {
            videoRef.current.play();
        }
    };

    const onError = () => {
        setStateVideoStatus(FJConstant.ERROR);
    };

    const isShowVideo = sourceLoadStatus === FJConstant.SUCCESS && stateVideoStatus === FJConstant.SUCCESS && isHover;

    return (
        <>
            {isTransparentBG && <canvas ref={transparentBgCanvasRef} className={styles.transparent_bg}></canvas>}
            <div
                className={classNames(styles.thumbnail_box, {
                    [styles.opacity_0]: loading || isShowVideo || sourceLoadStatus !== FJConstant.SUCCESS,
                })}
            >
                <img
                    className={classNames(styles.thumbnail_img)}
                    src={stateThumbnailUrl}
                    alt=''
                    onLoad={() => {
                        setSourceLoadStatus(FJConstant.SUCCESS);
                    }}
                    onError={() => {
                        setSourceLoadStatus(FJConstant.ERROR);
                    }}
                    loading='lazy'
                />
            </div>
            <div
                className={classNames(styles.video_box, {
                    [styles.opacity_0]: loading || !isShowVideo,
                })}
            >
                {isHover && (
                    <video
                        className={styles.video}
                        src={stateVideoUrl}
                        ref={videoRef}
                        loop={true}
                        muted={loading || !isShowVideo ? true : false}
                        onCanPlay={onCanPlay}
                        onError={onError}
                    />
                )}
            </div>
        </>
    );
});

FJVideoPreviewItem.propTypes = {
    videoUrl: PropTypes.string.isRequired,
    isLoading: PropTypes.bool,
    sourceLoadStatus: PropTypes.string.isRequired,
    setSourceLoadStatus: PropTypes.func.isRequired,
    thumbnailUrl: PropTypes.string.isRequired,
    isHover: PropTypes.bool.isRequired,
    isTransparentBG: PropTypes.bool,
};

FJVideoPreviewItem.displayName = 'FJVideoPreviewItem';
export default FJVideoPreviewItem;
